import React, { useState, useEffect, useMemo, useRef } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Flag from 'react-world-flags';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import "../../../assets/scss/components/ui/dropdawn/SortDropdown.scss";

const DROPDOWN_TYPES = {
  NORMAL: 'normal',
  NATIONALITY: 'nationality',
  PHONE: 'phone'
};

const countryPhoneCodes = {
  '1': 'us',
  '52': 'mx',
  '55': 'br',
  '56': 'cl',
  '57': 'co', 
  '58': 've',
  '591': 'bo',
  '593': 'ec',
  '595': 'py',
  '51': 'pe',
  '598': 'uy',
  '34': 'es',
};

export default function SortDropdown({ 
  value, 
  onChange, 
  options,
  type = DROPDOWN_TYPES.NORMAL,
  theme = 'dark',
  placeholder = 'Seleccionar...',
  // Nuevas props para funcionalidades avanzadas
  enableSearch = false,
  enablePagination = false,
  onSearch = null,
  searchPlaceholder = 'Buscar...',
  itemsPerPage = 5,
  totalItems = 0,
  currentPage = 1,
  onPageChange = null,
  searchTerm = '',
  debounceTime = 500,
  isLoading = false
}) {
  // Estados internos
  const [internalSearchTerm, setInternalSearchTerm] = useState(searchTerm);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  
  // Ref para el input de búsqueda
  const searchInputRef = useRef(null);

  // Verificar si la paginación debe estar activa
  const shouldUsePagination = enablePagination && totalItems > itemsPerPage;
  
  // Verificar si el valor actual es válido
  const isValidValue = options.some(option => option.value === value);
  const safeValue = isValidValue ? value : "";

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce((searchValue) => {
      if (onSearch) {
        onSearch(searchValue);
      }
    }, debounceTime),
    [onSearch, debounceTime]
  );

  // Efecto para el debounce del search
  useEffect(() => {
    if (enableSearch) {
      debouncedSearch(internalSearchTerm);
    }
    
    return () => {
      debouncedSearch.cancel();
    };
  }, [internalSearchTerm, debouncedSearch, enableSearch]);

  // Sincronizar searchTerm externo con el interno
  useEffect(() => {
    setInternalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Focus automático en el input de búsqueda cuando se abre el dropdown
  useEffect(() => {
    if (isOpen && enableSearch && searchInputRef.current) {
      // Pequeño delay para asegurar que el DOM esté listo
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, enableSearch]);

  const handleChange = (event) => {
    onChange(event.target.value);
    setIsOpen(false);
  };

  const handleSearchChange = (event) => {
    const newSearchTerm = event.target.value;
    setInternalSearchTerm(newSearchTerm);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSearchFocused(false);
  };

  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  // Manejar el foco del input de búsqueda
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    setIsSearchFocused(false);
  };

  // Prevenir navegación por teclado cuando el input está enfocado
  const handleKeyDown = (event) => {
    if (isSearchFocused && enableSearch) {
      // Permitir solo teclas de navegación específicas, evitar selección automática
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
        event.stopPropagation();
      }
      // Para otras teclas (letras, números, etc.), no hacer nada especial
      // Esto permite que el input funcione normalmente
    }
  };

  const getCountryCode = (option) => {
    if (type === DROPDOWN_TYPES.PHONE) {
      const phoneCode = option.country_code?.replace(/\D/g, '');
      return countryPhoneCodes[phoneCode] || null;
    }
    
    return option.iso_code?.toLowerCase() || option.country_code?.toLowerCase();
  };

  const renderMenuItem = (option) => {
    const isCountryRelated = type === DROPDOWN_TYPES.NATIONALITY || type === DROPDOWN_TYPES.PHONE;
    
    if (!isCountryRelated) {
      return (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      );
    }
    
    const countryCode = getCountryCode(option);

    return (
      <MenuItem key={option.value} value={option.value}>
        <div className={`GM__${theme}-dropdown-item-with-flag`}>
          {countryCode && (
            <Flag 
              code={countryCode} 
              height="16"
              width="24"
              className={`GM__${theme}-country-flag`}
            />
          )}
          <span className={`GM__${theme}-dropdown-item-label`}>
            {option.label}
          </span>
        </div>
      </MenuItem>
    );
  };

  const renderSearchHeader = () => {
    if (!enableSearch) return null;

    return (
      <Box 
        className={`GM__${theme}-dropdown-search-container`}
        onKeyDown={handleKeyDown}
      >
        <TextField
          ref={searchInputRef}
          fullWidth
          size="small"
          value={internalSearchTerm}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          placeholder={searchPlaceholder}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <Box className={`GM__${theme}-search-icon-container`}>
                <Search size={16} className={`GM__${theme}-search-icon`} />
              </Box>
            ),
          }}
          className={`GM__${theme}-search-input`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            // Prevenir que el Select maneje estas teclas cuando estamos escribiendo
            e.stopPropagation();
          }}
        />
      </Box>
    );
  };

  const renderPaginationControls = () => {
    if (!shouldUsePagination) return null;

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = ((currentPage - 1) * itemsPerPage) + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <Box className={`GM__${theme}-dropdown-pagination`}>
        <Box className={`GM__${theme}-pagination-info`}>
          <Typography variant="caption" className={`GM__${theme}-pagination-text`}>
            {`${startItem}-${endItem} de ${totalItems}`}
          </Typography>
        </Box>
        <Box className={`GM__${theme}-pagination-controls`}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handlePageChange(currentPage - 1);
            }}
            disabled={currentPage <= 1}
            className={`GM__${theme}-pagination-button`}
          >
            <ChevronLeft size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handlePageChange(currentPage + 1);
            }}
            disabled={currentPage >= totalPages}
            className={`GM__${theme}-pagination-button`}
          >
            <ChevronRight size={16} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderLoadingState = () => {
    if (!isLoading) return null;

    return (
      <MenuItem disabled>
        <Typography variant="body2" className={`GM__${theme}-loading-text`}>
          Cargando...
        </Typography>
      </MenuItem>
    );
  };

  const renderEmptyState = () => {
    if (options.length > 0 || isLoading) return null;

    return (
      <MenuItem disabled>
        <Typography variant="body2" className={`GM__${theme}-empty-text`}>
          {internalSearchTerm ? 'No se encontraron resultados' : 'No hay datos disponibles'}
        </Typography>
      </MenuItem>
    );
  };

  return (
    <div className={`GM__${theme}-sort-dropdown`}>
      <Select
        value={safeValue}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        open={isOpen}
        displayEmpty
        inputProps={{ "aria-label": "Seleccionar" }}
        className={`GM__${theme}-sort-select`}
        onKeyDown={handleKeyDown}
        MenuProps={{
          PaperProps: {
            className: `GM__${theme}-dropdown-paper`,
            style: { maxHeight: enableSearch || shouldUsePagination ? 400 : 200 }
          },
          MenuListProps: {
            className: `GM__${theme}-dropdown-menu-list`,
            onKeyDown: handleKeyDown
          }
        }}
      >
        <MenuItem value="" className={`GM__${theme}-placeholder-item`}>
          <em>{placeholder}</em>
        </MenuItem>
        
        {renderSearchHeader()}
        
        {renderLoadingState()}
        {renderEmptyState()}
        
        {!isLoading && options.map(renderMenuItem)}
        
        {renderPaginationControls()}
      </Select>
    </div>
  );
}

SortDropdown.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]).isRequired,
      label: PropTypes.string.isRequired,
      country_code: PropTypes.string,
      iso_code: PropTypes.string
    })
  ).isRequired,
  type: PropTypes.oneOf(Object.values(DROPDOWN_TYPES)),
  theme: PropTypes.oneOf(['dark', 'light']),
  placeholder: PropTypes.string,
  // Nuevas PropTypes
  enableSearch: PropTypes.bool,
  enablePagination: PropTypes.bool,
  onSearch: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  itemsPerPage: PropTypes.number,
  totalItems: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  searchTerm: PropTypes.string,
  debounceTime: PropTypes.number,
  isLoading: PropTypes.bool
};